/* eslint-disable no-console */
/* eslint-disable react/forbid-prop-types */

import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual } from 'react-redux';

import Label from '../../atoms/label/label';
import Button, { ButtonTheme } from '../../atoms/button/button';
import Modal, { ModalSize } from '../../molecules/modal/modal';
import { IconType } from '../../atoms/icon/icon';

import { storeStructure } from '../../../store/reducers';
import { composeURL, checkObjectAttributes } from '../../../utils/urlTools';
import { httpGet } from '../../../store/externalCalls/httpCalls';
import {
  DiffBlock,
  JsonBlock
} from './components';
import './formAuditChanges.scss';

const FormAuditChanges = (props) => {
  const {
    value, // uuid
    helpText,
    label,
    isForm,
    configFile,
    disabled,
    id,
  } = props;

  const session = useSelector((state) => state[storeStructure.Session], shallowEqual);

  const [openModal, setOpenModal] = useState(false);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);

  const sessionWithUuid = useMemo(() => ({ ...session, uuid: value }), [session, value]);

  const isUpdate = Boolean(payload?.isUpdate || payload?.oldData);
  const actionTitle = payload?.action || (isUpdate ? 'Update' : 'Create/Delete');

  const handlerOpenModal = () => {
    if (!value) return;
    if (!checkObjectAttributes(sessionWithUuid, configFile.sessionParams)) return;

    const url = composeURL(sessionWithUuid, configFile.sessionParams, configFile.getURL);

    setLoading(true);
    httpGet(url)
      .then((resp) => {
        setPayload(resp);
        setOpenModal(true);
      })
      .catch((e) => {
        console.error('Audit changes error:', e);
      })
      .finally(() => setLoading(false));
  };

  const handlerCloseModal = () => {
    setOpenModal(false);
    setPayload(null);
  };

  return (
    <div className={isForm === true ? 'form-group row' : ''}>
      {label !== '' && <Label helpText={helpText}>{label}</Label>}

      <div className={isForm === true ? 'col-sm-8' : ''}>
        <Button
          id={id}
          theme={ButtonTheme.INFO}
          icon={IconType.INFO}
          disabled={disabled || !value || loading}
          onClick={handlerOpenModal}
        />
      </div>

      {openModal === true && (
        <Modal
          title={`${configFile.title || 'Changes'} (${actionTitle})`}
          onClose={handlerCloseModal}
          size={ModalSize.XLARGE}
          dialogClassName="audit-modal-dialog"
        >
          <div className="audit-modal-body">
            {!payload && <div>Loading...</div>}

            {payload && (
              <>
                {!isUpdate && (
                  <JsonBlock title="Data" value={payload.data ?? payload} />
                )}
                {isUpdate && (
                  <>
                    <div className="audit-two-cols">
                      <div className="audit-two-cols__col">
                        <JsonBlock title="Old data" value={payload.oldData} />
                      </div>
                      <div className="audit-two-cols__col">
                        <JsonBlock title="New data" value={payload.data} />
                      </div>
                    </div>

                    <DiffBlock diff={payload.diff} />
                  </>
                )}
              </>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

FormAuditChanges.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  configFile: PropTypes.shape({
    title: PropTypes.string,
    getURL: PropTypes.string.isRequired,
    sessionParams: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  label: PropTypes.string,
  helpText: PropTypes.string,
  isForm: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

FormAuditChanges.defaultProps = {
  value: '',
  label: '',
  helpText: '',
  isForm: false,
  disabled: false,
  id: -1,
};

export default FormAuditChanges;
